import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  listQueue, removeQueued, replayQueue, pendingCount, failedCount,
} from "@/lib/offline";
import { listOfflineFiles, deleteOfflineFile } from "@/lib/offlineFiles";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import {
  RefreshCw, Trash2, CloudUpload, WifiOff, AlertTriangle, CheckCircle2,
  Image as ImageIcon, Clock,
} from "lucide-react";
import { toast } from "sonner";

export default function PendingSync() {
  const [items, setItems] = useState([]);
  const [files, setFiles] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);

  const refresh = async () => {
    setItems(listQueue());
    setFiles(await listOfflineFiles());
  };

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("fl-offline-change", onChange);
    window.addEventListener("fl-offline-files-change", onChange);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("fl-offline-change", onChange);
      window.removeEventListener("fl-offline-files-change", onChange);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const syncNow = async ({ includeFailed = false } = {}) => {
    if (!navigator.onLine) { toast.error("Still offline — sync will run when you're back in reception"); return; }
    setSyncing(true);
    const { synced, failed } = await replayQueue(api, { includeFailed });
    setSyncing(false);
    await refresh();
    if (synced > 0) toast.success(`${synced} change${synced > 1 ? "s" : ""} synced`);
    if (failed > 0) toast.error(`${failed} change${failed > 1 ? "s" : ""} rejected by the server — review below`);
    if (synced === 0 && failed === 0) toast.info("Nothing needed syncing");
  };

  const dropItem = (it) => {
    if (!window.confirm(`Delete this queued ${it.label?.toLowerCase() || "change"}? It will NOT be sent to the office.`)) return;
    removeQueued(it.id);
    refresh();
  };

  const dropFile = async (f) => {
    if (!window.confirm(`Delete this offline photo/file (${f.name})? Anything referencing it will sync without it.`)) return;
    await deleteOfflineFile(f.id);
    refresh();
  };

  const failed = items.filter((i) => i.failed_at);
  const queued = items.filter((i) => !i.failed_at);
  const empty = items.length === 0 && files.length === 0;

  return (
    <div data-testid="pending-sync-page" className="p-6 lg:p-10 max-w-3xl mx-auto pb-24">
      <PageHeader
        tag="/ Offline"
        title="Pending sync"
        subtitle="Everything you saved with no reception, waiting to reach the office."
      />

      <div className={`flex items-center gap-2 mb-5 text-xs font-bold uppercase tracking-widest ${online ? "text-emerald-700" : "text-zinc-500"}`} data-testid="sync-conn-status">
        {online ? <CheckCircle2 className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
        {online ? "Connected" : "Offline — changes are safe on this phone"}
      </div>

      {!empty && (
        <div className="flex gap-2 mb-6">
          <Button onClick={() => syncNow()} disabled={syncing || !online}
                  className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
                  data-testid="sync-now-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync now"}
          </Button>
          {failed.length > 0 && (
            <Button variant="outline" onClick={() => syncNow({ includeFailed: true })} disabled={syncing || !online}
                    className="rounded-sm uppercase tracking-wider font-bold text-xs border-amber-500 text-amber-700"
                    data-testid="retry-failed-btn">
              Retry failed ({failed.length})
            </Button>
          )}
        </div>
      )}

      {empty ? (
        <EmptyState title="All synced" description="Nothing is waiting on this phone — every change has reached the office." />
      ) : (
        <div className="space-y-6">
          {queued.length > 0 && (
            <section>
              <h2 className="font-display text-lg font-extrabold uppercase tracking-tight mb-3">
                Waiting to sync ({queued.length})
              </h2>
              <div className="space-y-2" data-testid="queued-list">
                {queued.map((it) => (
                  <QueueRow key={it.id} item={it} onDelete={() => dropItem(it)} />
                ))}
              </div>
            </section>
          )}

          {failed.length > 0 && (
            <section>
              <h2 className="font-display text-lg font-extrabold uppercase tracking-tight mb-1 text-amber-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Needs attention ({failed.length})
              </h2>
              <p className="text-xs text-zinc-500 mb-3">
                The office server rejected these. Fix the issue and retry, or delete them.
              </p>
              <div className="space-y-2" data-testid="failed-list">
                {failed.map((it) => (
                  <QueueRow key={it.id} item={it} failed onDelete={() => dropItem(it)} />
                ))}
              </div>
            </section>
          )}

          {files.length > 0 && (
            <section>
              <h2 className="font-display text-lg font-extrabold uppercase tracking-tight mb-3 flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> Photos & signatures on this phone ({files.length})
              </h2>
              <div className="space-y-2" data-testid="offline-files-list">
                {files.map((f) => (
                  <div key={f.id} className="bg-white border border-zinc-300 p-3 flex items-center gap-3" data-testid={`offline-file-${f.id}`}>
                    <span className="w-9 h-9 bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0">
                      <CloudUpload className="w-4 h-4 text-zinc-500" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">{f.name}</div>
                      <div className="text-[11px] text-zinc-500 font-mono">
                        {(f.size / 1024).toFixed(0)} KB · {new Date(f.created_at).toLocaleString("en-NZ")}
                      </div>
                    </div>
                    <button onClick={() => dropFile(f)} className="text-zinc-400 hover:text-red-600 p-2" data-testid={`offline-file-delete-${f.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function QueueRow({ item, failed = false, onDelete }) {
  return (
    <div className={`bg-white border p-3 flex items-center gap-3 ${failed ? "border-amber-400" : "border-zinc-300"}`}
         data-testid={`sync-item-${item.id}`}>
      <span className={`w-9 h-9 flex items-center justify-center shrink-0 border ${failed ? "bg-amber-50 border-amber-200 text-amber-600" : "bg-zinc-100 border-zinc-200 text-zinc-500"}`}>
        {failed ? <AlertTriangle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold">{item.label || "Change"}</div>
        <div className="text-[11px] text-zinc-500 font-mono">
          Saved {new Date(item.queued_at).toLocaleString("en-NZ")}
        </div>
        {failed && item.error && (
          <div className="text-[11px] text-amber-700 mt-1">{item.error}</div>
        )}
      </div>
      <button onClick={onDelete} className="text-zinc-400 hover:text-red-600 p-2" data-testid={`sync-item-delete-${item.id}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}
